import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react';

export interface WpCheckSummaryItem {
    name: string;
    yoa: string;
    lastYoa: string;
    currentBalanced: boolean;
    lastYearBalanced: boolean;
    ignoreLastYear: boolean;
    /** UnBalanced but |control| in (0, 1] → toolbar chip warning (yellow). */
    currentSoftUnbalanced?: boolean;
    /** UnBalanced but |control| in (0, 1] → toolbar chip warning (yellow). */
    lastYearSoftUnbalanced?: boolean;
}

interface RegisteredSummary extends WpCheckSummaryItem {
    order: number;
}

interface WpCheckSummaryContextValue {
    register: (id: string, item: WpCheckSummaryItem) => void;
    unregister: (id: string) => void;
    summaries: WpCheckSummaryItem[];
}

const WpCheckSummaryContext = createContext<WpCheckSummaryContextValue | null>(null);

export function WpCheckSummaryProvider({children}: {children: React.ReactNode}) {
    const orderRef = useRef(0);
    const [registry, setRegistry] = useState<Map<string, RegisteredSummary>>(new Map());

    const register = useCallback((id: string, item: WpCheckSummaryItem) => {
        setRegistry((prev) => {
            const existing = prev.get(id);
            if (
                existing &&
                existing.name === item.name &&
                existing.yoa === item.yoa &&
                existing.lastYoa === item.lastYoa &&
                existing.currentBalanced === item.currentBalanced &&
                existing.lastYearBalanced === item.lastYearBalanced &&
                existing.ignoreLastYear === item.ignoreLastYear &&
                Boolean(existing.currentSoftUnbalanced) === Boolean(item.currentSoftUnbalanced) &&
                Boolean(existing.lastYearSoftUnbalanced) === Boolean(item.lastYearSoftUnbalanced)
            ) {
                return prev;
            }
            const next = new Map(prev);
            next.set(id, {
                ...item,
                order: existing?.order ?? orderRef.current++,
            });
            return next;
        });
    }, []);

    const unregister = useCallback((id: string) => {
        setRegistry((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const summaries = useMemo(
        () => [...registry.values()].sort((a, b) => a.order - b.order).map(({order: _order, ...item}) => item),
        [registry],
    );

    const value = useMemo(
        () => ({register, unregister, summaries}),
        [register, unregister, summaries],
    );

    return (
        <WpCheckSummaryContext.Provider value={value}>
            {children}
        </WpCheckSummaryContext.Provider>
    );
}

export function useWpCheckSummaryRegistry() {
    return useContext(WpCheckSummaryContext);
}

export function useRegisterWpCheckSummary(item: WpCheckSummaryItem) {
    const ctx = useWpCheckSummaryRegistry();
    const register = ctx?.register;
    const unregister = ctx?.unregister;
    const id = React.useId();

    React.useEffect(() => {
        if (!unregister) return;
        return () => unregister(id);
    }, [unregister, id]);

    React.useEffect(() => {
        if (!register) return;
        register(id, item);
    }, [
        register,
        id,
        item.name,
        item.yoa,
        item.lastYoa,
        item.currentBalanced,
        item.lastYearBalanced,
        item.ignoreLastYear,
        item.currentSoftUnbalanced,
        item.lastYearSoftUnbalanced,
    ]);
}
